import React, {Fragment, useMemo} from 'react';
import messages from './messages';
import {
  RelatedProductRow,
  useRelatedProductsForm,
} from './use-related-products-form';
import Icon from '@ailibs/feather-react-ts';
import AppBarActions from 'components/VerticalLayout/app-bar-actions';
import AsyncItemPicker from 'components/async-item-picker';
import Button from 'components/button';
import SectionSeparator from 'components/separator/section-separator';
import Can from 'features/hylian-shield/can';
import {useTranslation} from 'react-i18next';
import {Row, Col, Card} from 'reactstrap';
import getVariantName from 'utils/row-getters/get-variant-name';
import {CompactVariant} from 'types/compact-variant';

const RelatedProducts = () => {
  const {t} = useTranslation();
  const {
    rows,
    subject,
    isSubmitting,
    validationErrors,
    addRow,
    removeRow,
    updateRow,
    filterOption,
    submit,
  } = useRelatedProductsForm();

  const groupedRows = useMemo(() => {
    const groups: Record<string, RelatedProductRow[]> = {};

    Object.values(rows).forEach(p => {
      const collectionName: string =
        p.variant?.collection_name ??
        t(...messages.noCollection()) ??
        'Sans collection';
      if (!groups[collectionName]) {
        groups[collectionName] = [];
      }
      groups[collectionName].push(p);
    });

    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) =>
        (a.variant?.name ?? '').localeCompare(b.variant?.name ?? '')
      );
    });

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [rows, t]);

  return (
    <Card className="p-3">
      <Row className="mb-3">
        <Col>
          <SectionSeparator title={t(...messages.title())} />
        </Col>
        <Col md="auto">
          <Can action="edit" subject={subject}>
            <Button color="important" onClick={addRow}>
              <Icon name="plus" className="me-1" size={18} />
              {t(...messages.addProduct())}
            </Button>
          </Can>
        </Col>
      </Row>

      {(() => {
        let index = 0;
        return groupedRows.map(([collectionName, items]) => (
          <Fragment key={collectionName}>
            <Row className="mb-3 mt-4">
              <Col>
                <h6 className="text-uppercase fw-bold mb-1 text-primary">
                  {collectionName}
                </h6>
                <hr className="mt-0 text-primary" />
              </Col>
            </Row>
            {items.map(p => {
              const i = index++;
              return (
                <Row className="mb-3" key={p.id}>
                  <Col>
                    <AsyncItemPicker
                      label={t(...messages.product())}
                      filterOption={filterOption}
                      getOptionLabel={(xx: CompactVariant) =>
                        getVariantName(xx)
                      }
                      menuPlacement="top"
                      endPoint="data/components-with-self-composed"
                      errors={validationErrors[`variants.${i}.variant_id`]}
                      value={p.variant}
                      onChange={x => updateRow(p, x)}
                    />
                  </Col>
                  <Col className="align-items-end d-flex" md={1}>
                    <Can action="edit" subject={subject}>
                      <Button
                        color="link"
                        className="text-danger"
                        onClick={() => removeRow(p.id)}
                      >
                        <Icon name="trash-2" size={18} />
                      </Button>
                    </Can>
                  </Col>
                </Row>
              );
            })}
          </Fragment>
        ));
      })()}

      <Row>
        <Col md={12} className="mb-4">
          <Can action="edit" subject={subject}>
            <AppBarActions>
              <Button
                isLoading={isSubmitting}
                color="important"
                onClick={submit}
              >
                <Icon name="check" size={15} className="me-1" />
                {t('common.saveChanges')}
              </Button>
            </AppBarActions>
          </Can>
        </Col>
      </Row>
    </Card>
  );
};

export default RelatedProducts;
